import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { BASIC_DARK_GRAY } from "../../../constants/colors";
import { Menu } from "../../../ducks/menu";
import { useAsset } from "../../../hooks/useAsset";
import { Button } from "../../atoms/Button";
import { FavoriteIcon } from "../../atoms/FavoriteIcon";
import { MenuCountSelect } from "../../molecules/menu/MenuCountSelect";
import { MenuPrice } from "../../molecules/menu/MenuPrice";

type Props = {
  isShow: boolean;
  menu: Menu;
  isLimitedMenuSelected: boolean;
  onClose: () => void;
  onSelectMenu: (
    amount: number,
    optionId?: number,
    optionName?: string,
  ) => void;
  onFavorite: (isFavorite: boolean) => void;
};

export const MenuDetailPopup: React.FC<Props> = ({
  isShow,
  menu,
  isLimitedMenuSelected,
  onClose,
  onSelectMenu,
  onFavorite,
}) => {
  const { baseDir } = useAsset();
  const menuDetailRef = useRef<HTMLDivElement>(null);

  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const [shouldReset, setShouldReset] = useState(false);
  const [selectedItems, setSelectedItems] = useState<
    {
      amount: number;
      optionId?: number;
      optionName?: string;
    }[]
  >([]);

  const shouldDisable = useMemo(() => {
    return menu.isPreorder && !menu.isEnableButton;
  }, [menu]);

  useEffect(() => {
    if (isShow) {
      setSelectedItems([]);
      document.body.classList.add("overflow-hidden");
      const wrpr = menuDetailRef.current;
      if (wrpr) {
        wrpr.scrollTop = 0;
      }
    } else {
      document.body.classList.remove("overflow-hidden");
    }
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [isShow]);

  useEffect(() => {
    const totalQuantity = selectedItems.reduce(
      (acc, item) => acc + item.amount,
      0,
    );
    setIsEmpty(totalQuantity === 0);
  }, [selectedItems]);

  const handleShouldReset = useCallback(() => {
    setShouldReset(true);
    setTimeout(() => {
      setShouldReset(false);
    }, 100);
  }, []);

  const handleChangeAmount = useCallback(
    (amount: number, optionId?: number, optionName?: string) => {
      const isAlreadyAdded = selectedItems.findIndex(
        (item) => item.optionId === optionId,
      );

      if (isAlreadyAdded === -1) {
        setSelectedItems([
          ...selectedItems,
          {
            amount,
            optionId,
            optionName,
          },
        ]);
      } else {
        const updatedItems = selectedItems.map((item) => {
          if (item.optionId === optionId) {
            return {
              amount,
              optionId,
              optionName,
            };
          }
          return item;
        });
        setSelectedItems(updatedItems);
      }
    },
    [selectedItems],
  );

  const handleAddToCart = useCallback(() => {
    selectedItems.forEach((item) => {
      onSelectMenu(item.amount, item.optionId, item.optionName);
    });

    setSelectedItems([]);
    handleShouldReset();
    onClose();
  }, [selectedItems, onSelectMenu]);

  const handleClose = useCallback(() => {
    setSelectedItems([]);
    handleShouldReset();
    onClose();
  }, [onClose]);

  return (
    <MenuDetailModal ref={menuDetailRef} $isShow={isShow}>
      <MenuDetailModalContainer>
        <MenuDetailImage>
          <img
            style={{ display: "none" }}
            src={
              menu.imageUrl && hasLoaded && !hasError
                ? menu.imageUrl
                : baseDir + "/assets/images/menu_dummy@2x.jpg"
            }
            alt="menu"
            onLoad={() => setHasLoaded(true)}
            onError={() => setHasError(true)}
          />
          <MenuDetailImageThumb
            src={
              menu.imageUrl && hasLoaded && !hasError
                ? menu.imageUrl
                : baseDir + "/assets/images/menu_dummy@2x.jpg"
            }
          />
        </MenuDetailImage>

        <MenuDetailInfo>
          <MenuDetailTextContainer>
            <MenuNameText>{menu.name}</MenuNameText>

            <MenuPrice
              price={menu.campaign ? menu.campaign.price : menu.price}
              priceWithTax={
                menu.campaign ? menu.campaign.priceWithTax : menu.priceWithTax
              }
            />
          </MenuDetailTextContainer>
          <FavoriteIcon
            type="heart"
            favorite={menu.isFavorite}
            onClick={() => onFavorite(!menu.isFavorite)}
          />
        </MenuDetailInfo>

        <MenuDetailArea>
          <MenuDetailAreaText>{menu.details}</MenuDetailAreaText>
        </MenuDetailArea>

        <>
          <MenuDetailDefaultQuantity $enable={!shouldDisable}>
            <OptionQuantityWrapper>
              <QuantityOptionLabel>
                {menu.options.length > 0 ? "オプションなし" : ""}
              </QuantityOptionLabel>

              <MenuCountSelect
                initialCount={menu.isLimitedMenu ? 1 : 0}
                onChange={(count) => handleChangeAmount(count)}
                limited={menu.isLimitedMenu}
                limitedDisabled={isLimitedMenuSelected}
                shouldReset={shouldReset}
              />
            </OptionQuantityWrapper>
          </MenuDetailDefaultQuantity>

          {menu.options.map((option) => (
            <MenuDetailOptionArea $enable={!shouldDisable} key={option.id}>
              <OptionQuantityWrapper>
                <QuantityOptionLabel>{option.name}</QuantityOptionLabel>

                <MenuCountSelect
                  initialCount={menu.isLimitedMenu ? 1 : 0}
                  onChange={(count) =>
                    handleChangeAmount(count, option.id, option.name)
                  }
                  limited={menu.isLimitedMenu}
                  limitedDisabled={isLimitedMenuSelected}
                  shouldReset={shouldReset}
                />
              </OptionQuantityWrapper>
            </MenuDetailOptionArea>
          ))}
        </>

        {menu.isPreorder && (
          <MenuDetailArea>
            <MenuDetailAreaText>{menu.preorderMessage}</MenuDetailAreaText>
          </MenuDetailArea>
        )}

        <MenuDetailButtonContainer>
          {shouldDisable ? (
            <Button text="予約商品" size="lg" disabled btnStyle="disabled" />
          ) : (
            <Button
              disabled={isEmpty}
              text="カートに入れる"
              size="lg"
              btnStyle="primary"
              onClick={handleAddToCart}
            />
          )}

          <Button
            text="メニューへ戻る"
            size="lg"
            btnStyle="secondary"
            onClick={handleClose}
          />
        </MenuDetailButtonContainer>
      </MenuDetailModalContainer>
    </MenuDetailModal>
  );
};

const MenuDetailModal = styled.div<{ $isShow: boolean }>`
  position: fixed;
  top: 52px;
  left: 0;
  width: 100%;
  height: calc(100% - 51px);
  background-color: #ffffff;
  transform: translate(0, ${({ $isShow }) => ($isShow ? "0" : "100%")});
  opacity: ${({ $isShow }) => ($isShow ? "1" : "0")};
  transition: all 0.5s cubic-bezier(0.13, 0.79, 0.36, 0.97);
  overflow: scroll;
  z-index: 1002;
`;

const MenuDetailModalContainer = styled.div`
  max-width: 980px;
  margin: 0 auto;
  padding: 0;
`;

const MenuDetailImageThumb = styled.div<{ src: string }>`
  width: 100%;
  padding-top: 60%;
  background-position: center center;
  background-size: cover;
  background-image: ${({ src }) => `url(${src})`};
`;

const MenuDetailImage = styled.div`
  margin: 0 auto 28px auto;
`;

const MenuDetailInfo = styled.div`
  display: flex;
  padding: 0 16px;
  margin: 0 auto 15px auto;
`;

const MenuDetailTextContainer = styled.div`
  flex: 1;
  color: ${BASIC_DARK_GRAY};
`;

const MenuNameText = styled.div`
  font-size: 20px;
  font-weight: 500;
  line-height: 1em;
  text-align: left;
  color: ${BASIC_DARK_GRAY};
  margin: 0 0 16px 0;
  padding: 0;
`;

const OptionQuantityWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const QuantityOptionLabel = styled.p`
  text-align: left;
  padding: 0;
  margin: 0;
  flex: 1;
  font-size: 16px;
  font-weight: 400;
`;

const MenuDetailArea = styled.div`
  max-width: 100%;
  padding: 0 16px;
  font-weight: 300;
  margin: 0 auto 35px auto;
`;

const MenuDetailAreaText = styled.p`
  margin: 0;
  padding: 0;
  font-size: 16px;
  line-height: 1.6em;
  text-align: left;
  color: ${BASIC_DARK_GRAY};
  white-space: pre-line;
`;

const MenuDetailDefaultQuantity = styled.div<{ $enable: boolean }>`
  max-width: 100%;
  padding: 0 16px;
  margin: 0 auto 30px auto;
  opacity: ${({ $enable }) => ($enable ? "1" : "0.25")};
  pointer-events: ${({ $enable }) => ($enable ? "auto" : "none")};
`;

const MenuDetailOptionArea = styled.div<{ $enable: boolean }>`
  max-width: 100%;
  padding: 0 16px;
  margin: 0 auto 30px auto;
  opacity: ${({ $enable }) => ($enable ? "1" : "0.25")};
  pointer-events: ${({ $enable }) => ($enable ? "auto" : "none")};
`;

const MenuDetailButtonContainer = styled.div`
  width: 100%;
  margin-bottom: 50px;
  padding: 0;
  display: flex;
  justify-content: center;
  gap: 30px;
  align-items: center;
  flex-direction: column;
  background-color: #ffffff;
  button {
    height: auto;
    line-height: 1;
    padding: 20px 0 18px;
    font-weight: 600;
  }
`;
